import React, { useState } from 'react'
import SbEditable from 'storyblok-react'
import {
  BasicTextInput,
  type TextInputBlok,
} from '@/components/block/BasicTextInput'
import {
  Checkbox as CheckboxBlock,
  type CheckboxBlok,
} from '@/components/block/Checkbox'
import { Dropdown, type DropdownBlok } from '@/components/block/Dropdown'
import { Textarea, type TextareaBlok } from '@/components/block/Textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Label, type LabelBlok } from '@/components/block/Label'
import resolveLink from '@/utils/resolveLink'

type Field = TextInputBlok | CheckboxBlok | DropdownBlok | TextareaBlok | LabelBlok

type StoryblokComponent = {
  _uid: string
  component: string
  marginTop?: number
  marginBottom?: number
}

export interface InlineFormProps {
  blok: StoryblokComponent & {
    id: string
    action: string
    form_name: string
    header_title: string
    description?: string
    privacy_policy_link?: string
    privacy_policy_label?: string
    checkbox_text: string
    submit_text: string
    fields: Field[]
  }
}

const fieldComponents = {
  basic_input_text: BasicTextInput,
  checkbox: CheckboxBlock,
  dropdown: Dropdown,
  textarea: Textarea,
  label: Label,
}

export const InlineForm = ({ blok }: InlineFormProps) => {
  const [agreed, setHasAgreed] = useState(false)

  const renderField = (field: Field, index: number) => {
    const Component =
      fieldComponents[field.component as keyof typeof fieldComponents]
    return Component ? (
      <Component key={field._uid || index} blok={field} />
    ) : null
  }

  const marginStyles = {
    marginTop: blok.marginTop ? `${blok.marginTop}px` : undefined,
    marginBottom: blok.marginBottom ? `${blok.marginBottom}px` : undefined,
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const form = e.target as HTMLFormElement
    const formData = new FormData(form)

    try {
      const response = await fetch(blok.action, {
        method: 'POST',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8',
          'Content-Type': 'application/x-www-form-urlencoded',
          'Origin': blok.action,
          'Upgrade-Insecure-Requests': '1'
        },
        body: new URLSearchParams(formData as any).toString()
      })

      console.log('Response:', response)

      if (response.ok) {
        window.location.href = '/contact-us-thank-you/'
      }
    } catch (error) {
      console.log('Error:', error)
      console.error('Form submission error:', error)
    }
  }

  return (
    <div className="font-display max-w-4xl p-4 mx-auto" style={marginStyles}>
      {/** @ts-ignore: Storyblok typing issue with children **/}
      <SbEditable content={blok}>
        <h2 className="text-5xl font-semibold leading-tight mb-8">
          {blok.header_title}
        </h2>
        {blok.description && (
          <p className="text-md text-darkblue mt-10 mb-12">
            {blok.description}
          </p>
        )}
        <form name={blok.form_name} id={blok.id} onSubmit={handleSubmit} method="post">
          <div className="flex flex-col justify-start gap-6">
            {blok.fields?.map(renderField)}
            <Checkbox
              style="style2"
              checked={agreed}
              onChange={(checked: boolean) => setHasAgreed(checked)}
            >
              {blok.checkbox_text}{' '}
              {blok.privacy_policy_link && blok.privacy_policy_label && (
                <a
                  href={resolveLink(blok.privacy_policy_link)}
                  className="text-brightgreen underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {blok.privacy_policy_label}
                </a>
              )}
            </Checkbox>
          </div>
          <div className="flex justify-center md:justify-end mt-10">
            <Button type="submit" variant="secondary_blue" disabled={!agreed}>
              {blok.submit_text}
            </Button>
          </div>
        </form>
      </SbEditable>
    </div>
  )
}

InlineForm.displayName = 'Inline Form'
